import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card } from 'react-bootstrap';

import { http } from '../../features/jobs/api/http';
import { fetchJob } from '../../features/jobs/api/jobsApi';
import { rebuildStandardDataFromJob } from '../../helpers/RebuildStandardDataFromJob';
import CreateWizzardForm from '../../components/project-management/create-new/CreateWizzardForm';

// RECONCILE WIZARD — open ≥2 launched products together in the REAL wizard
// (CreateWizzardForm mode="edit" + reconcile flag) so every card / execution across
// ALL phases and ALL states is visible with full detail. Produced drums render
// locked with a "Belongs to product" swap control (+ optional Relaunch). Submit
// collects only the swap map and applies it via reallocate-produced — the create/
// edit machinery is untouched. Opened from Execution History or the queue
// multi-select with { jobIds, machineId, returnTo } in the navigation state.
const ReconcileWizzard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const jobIds = Array.isArray(location?.state?.jobIds)
    ? location.state.jobIds.map(String)
    : [];
  const machineId = location?.state?.machineId || '';
  const returnTo =
    location?.state?.returnTo || '/apps/production-management/projects';

  const [standardsData, setStandardsData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!jobIds.length) {
        setLoadErr('Select at least one product.');
        return;
      }
      try {
        setLoading(true);
        const items = [];
        for (const jid of jobIds) {
          try {
            const job = await fetchJob(jid);
            if (!job?.jobId) continue;
            const sd = (rebuildStandardDataFromJob(job, [], '', {
              editMode: true,
              reconcileMode: true
            }) || [])[0];
            if (sd) items.push(sd);
          } catch {
            /* skip a product that fails to load */
          }
        }
        if (!alive) return;
        if (!items.length) throw new Error('No products could be loaded.');
        for (const it of items) it._mergeGroupJobIds = [...jobIds];
        setStandardsData(items);
      } catch (e) {
        if (alive) setLoadErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // jobIds is derived from the (stable) navigation state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearStandardsData = useCallback(() => {
    setStandardsData(null);
    navigate(returnTo);
  }, [navigate, returnTo]);

  const handleReconcileSubmit = useCallback(
    async ({ standardData, reconcile = {}, reconcileLengths = {} }) => {
      setSubmitErr('');
      const products = Array.isArray(standardData) ? standardData : [standardData];

      // ── corrected drum shares ──────────────────────────────────────────────
      // Keyed `jobId::tokenId`, because a merged bobbin has one share PER
      // PRODUCT: the same tokenId is in both jobs, each holding what that
      // product got off the drum. This is the only reconcile action available
      // for a merged drum — it cannot be moved to the product it is already on.
      const lengthItems = Object.entries(reconcileLengths)
        .map(([key, raw]) => {
          const [jobId, tokenId] = String(key).split('::');
          // European decimals: the operator types 2060,5
          const producedLength = Number(String(raw).replace(',', '.'));
          return { jobId, tokenId, producedLength };
        })
        .filter(
          it =>
            it.jobId &&
            it.tokenId &&
            Number.isFinite(it.producedLength) &&
            it.producedLength >= 0
        );

      // The PHYSICAL position of each produced drum in the wizard IS the state: a
      // drum whose current product (where its card sits now) differs from its
      // `_reconcileOrigin` was moved → source=origin, target=current. `reconcile`
      // only carries the per-drum relaunch flag now.
      const items = [];
      // Colour relabels of shared bobbins, applied to every product holding
      // them. Collected separately because they are not moves.
      const recolours = [];
      const seen = new Set();
      for (const p of products) {
        const current = String(p._id || p._parentJobId || '');
        for (const ph of p.phases || []) {
          const execs = [
            ...(Array.isArray(ph.execution) ? ph.execution : []),
            ...((ph.conductors || []).flatMap(c =>
              Array.isArray(c.execution) ? c.execution : []
            ))
          ];
          for (const ex of execs) {
            if (!ex?.tokenId || !ex._locked) continue;
            const tokenId = String(ex.tokenId);
            if (seen.has(tokenId)) continue; // merged token appears in >1 product
            const origin = ex._reconcileOrigin
              ? String(ex._reconcileOrigin)
              : current;
            const relaunch = !!reconcile[tokenId]?.relaunch;
            // colour inversion: current colour differs from the loaded one
            const originColor =
              ex._reconcileOriginColor != null
                ? String(ex._reconcileOriginColor)
                : null;
            const curColor = ex.color != null ? String(ex.color) : null;
            const recoloured =
              originColor != null && curColor != null && curColor !== originColor;
            // wire-Ø re-slot (unwinding etc.): current diameter differs from loaded
            const originDia =
              ex._reconcileOriginWireDiameter != null
                ? Number(ex._reconcileOriginWireDiameter)
                : null;
            const curDia =
              ex.wireDiameter != null ? Number(ex.wireDiameter) : null;
            const rediametered =
              originDia != null && curDia != null && curDia !== originDia;
            if (origin === current && !relaunch && !recoloured && !rediametered)
              continue;
            seen.add(tokenId);
            const item = {
              sourceJobId: origin,
              tokenId,
              targetJobId: current,
              relaunch
            };
            // A colour belongs to the BOBBIN, so a merged drum's relabel has to
            // reach every product sharing it — `reallocate-produced` only ever
            // touches one job. Those go to /jobs/recolor-drums instead; the rest
            // ride along on the move, as they always have.
            if (recoloured) {
              if (ex._merged) recolours.push({ tokenId, color: curColor });
              else item.targetColor = curColor;
            }
            if (rediametered) item.targetWireDiameter = curDia;
            // A drum that only changed colour has nothing to move.
            if (origin === current && !relaunch && !rediametered && ex._merged) continue;
            items.push(item);
          }
        }
      }

      if (!items.length && !lengthItems.length && !recolours.length) {
        navigate(returnTo);
        return;
      }
      try {
        // Lengths first: a re-slice is a statement about drums that exist, and
        // applying it before anything moves keeps the numbers attached to the
        // product they were typed against.
        if (lengthItems.length) {
          const { data } = await http.post('/api/redis/jobs/reconcile-lengths', {
            items: lengthItems,
            reason: 'reconcile wizard'
          });
          // Rejections are the whole point of the endpoint reporting them — a
          // silent partial apply is how a reconcile leaves things worse.
          if (data?.rejected?.length) {
            setSubmitErr(
              `Some shares were refused: ${data.rejected
                .map(r => `${r.tokenId || r.jobId}: ${r.reason}`)
                .join('; ')}`
            );
            return;
          }
        }
        // Colours before moves: a relabel is about the bobbin as it stands, and
        // applying it first means the move that follows carries the right one.
        if (recolours.length) {
          const { data } = await http.post('/api/redis/jobs/recolor-drums', {
            items: recolours,
            reason: 'reconcile wizard'
          });
          if (data?.rejected?.length) {
            setSubmitErr(
              `Some colours were refused: ${data.rejected
                .map(r => `${r.tokenId}: ${r.reason}`)
                .join('; ')}`
            );
            return;
          }
        }
        if (items.length) {
          await http.post(
            `/api/redis/queues/${machineId}/reallocate-produced`,
            { items, dryRun: false }
          );
        }
        navigate(returnTo);
      } catch (e) {
        setSubmitErr(
          e?.response?.data?.error || e?.message || 'Reconcile failed.'
        );
      }
    },
    [machineId, navigate, returnTo]
  );

  if (standardsData) {
    return (
      <>
        {submitErr && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            {submitErr}
          </Alert>
        )}
        <CreateWizzardForm
          standardData={standardsData}
          setStandardsData={setStandardsData}
          clearStandardsData={clearStandardsData}
          handleSubmitFc={handleReconcileSubmit}
          mode="edit"
          reconcile
        />
      </>
    );
  }

  return (
    <Card className="mb-9 mt-3">
      <Card.Body>
        <h3 className="mb-2">Reconcile products</h3>
        {loadErr ? (
          <Alert variant="danger" className="py-2 mb-0">
            {loadErr}
          </Alert>
        ) : (
          <div className="text-muted">
            {loading ? 'Loading products…' : 'Preparing reconcile wizard…'}
          </div>
        )}
        <div>
          <Button
            className="mt-3"
            variant="phoenix-secondary"
            onClick={() => navigate(returnTo)}
          >
            Back
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ReconcileWizzard;
